import * as React from "react"
import { Container, Table, Row, Col, Spinner, Button, Form } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import PoolStatisticsDetails from "../../../api/Randoms/RandomsModel/PoolStatisticsDetails"
import PoolStatisticsDetailsGetObservable from "../../../api/Randoms/PoolStatisticsDetailsGet"
import RunRandomSelectionMessage from "../../../api/Randoms/RandomsModel/RunRandomSelectionMessage"
import RunRandomSelectionMessagesGetObservable from "../../../api/Randoms/RunRandomSelectionMessagesGet"
import RandomSelectionRunData from "../../../api/Randoms/RandomsModel/RandomSelectionRunData"
import RandomSelectionRunObservable from "../../../api/Randoms/RandomSelectionRun"
import ConfirmationModal from "../../ConfirmationModal"
import FormatDate from "../../../formatters/FormatDate"
import RandomSelectionList from "../RandomSelectionList"
import PoolSelectionListGetObservable from "../../../api/Randoms/PoolSelectionListGet"
import PoolSelectionRow from "../../../api/Randoms/RandomsModel/PoolSelectionRow"

interface RunRandomProps {
  poolId: number
  poolName: string
  isCompanyAdmin?: boolean
  isPortalAdmin?: boolean
  onRunFinish: () => void
}

interface RunRandomValidity {
  isActualToSelectDrugValid: boolean
  isActualToSelectAlcoholValid: boolean
  isAlternateDrugValid: boolean
  isAlternateAlcoholValid: boolean
}

interface RunRandomData {
  actualToSelectDrug?: number
  actualToSelectAlcohol?: number
  alternateDrug?: number
  alternateAlcohol?: number
}

interface RunRandomState extends RunRandomData, RunRandomValidity {
  step: "run" | "success"
  saving: boolean
  statisticsLoading: boolean
  messagesLoading: boolean
  poolSelectionLoading: boolean
  statistics?: PoolStatisticsDetails
  messages?: RunRandomSelectionMessage
  runResults?: RandomSelectionRunData[]
  poolSelection?: PoolSelectionRow
  showRunConfirmation: boolean
  showSelectionList: boolean
}

export default class RunRandom extends React.PureComponent<RunRandomProps, RunRandomState> {
  state: RunRandomState = {
    step: "run",
    saving: false,
    statisticsLoading: false,
    messagesLoading: false,
    poolSelectionLoading: false,
    isActualToSelectDrugValid: true,
    isActualToSelectAlcoholValid: true,
    isAlternateDrugValid: true,
    isAlternateAlcoholValid: true,
    showRunConfirmation: false,
    showSelectionList: false
  }

  subscriptions = new Subscription()

  componentDidMount() {
    this.setState({ statisticsLoading: true, messagesLoading: true })
    this.subscriptions.add(
      PoolStatisticsDetailsGetObservable(this.props.poolId).subscribe(
        data => this.setState({ statistics: data, actualToSelectDrug: data.DrugRequired, actualToSelectAlcohol: data.AlcoholRequired, statisticsLoading: false }),
        () => this.setState({ statisticsLoading: false })
      ))
    this.subscriptions.add(
      RunRandomSelectionMessagesGetObservable(this.props.poolId).subscribe(
        data => this.setState({ messages: data, messagesLoading: false }),
        () => this.setState({ messagesLoading: false })
      ))
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  validateSizeValue = (value: number | undefined) => !value || (value >= 0 && (!this.state.statistics || value <= this.state.statistics.CurrentPoolSize))

  handleSizeChange = (what: keyof RunRandomData, validityField: keyof RunRandomValidity, value: string) => {
    value = value.replace(/\D/g, "")
    if (value.length === 0) {
      this.setState({ ...{}, [what]: undefined, [validityField]: true })
    } else {
      const numberValue = Number(parseInt(value))
      if (numberValue !== undefined) {
        this.setState({ ...{}, [what]: numberValue, [validityField]: this.validateSizeValue(numberValue) })
      }
    }
  }

  handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const { actualToSelectDrug, actualToSelectAlcohol, alternateDrug, alternateAlcohol, statistics } = this.state
    if (!statistics) {
      return
    }
    const validity: RunRandomValidity = {
      isActualToSelectDrugValid: this.validateSizeValue(actualToSelectDrug),
      isActualToSelectAlcoholValid: this.validateSizeValue(actualToSelectAlcohol),
      isAlternateDrugValid: !statistics?.GenerateAlternates || this.validateSizeValue(alternateDrug),
      isAlternateAlcoholValid: !statistics?.GenerateAlternates || this.validateSizeValue(alternateAlcohol)
    }
    const isValid = Object.values(validity).every((valid: boolean) => valid)

    if (isValid) {
      this.setState({ showRunConfirmation: true })
    } else {
      this.setState(validity)
    }
  }

  runSelection = () => {
    this.setState({ showRunConfirmation: false, saving: true })
    // TODO: Pass correct contactId and updateMethodID values once authorization and account data will be available
    this.subscriptions.add(
      RandomSelectionRunObservable({
        ContactId: 0,
        UpdateMethodID: 0,
        PoolId: this.props.poolId,
        RequiredDrug: this.state.actualToSelectDrug || 0,
        RequiredAlcohol: this.state.actualToSelectAlcohol || 0,
        AlternateDrug: this.state.alternateDrug || 0,
        AlternateAlcohol: this.state.alternateAlcohol || 0
      }).subscribe(
        data => this.setState({ runResults: data, saving: false, step: "success" }),
        (error) => {
          this.setState({ saving: false })
          toast.error(error && error.errorData && error.errorData.message)
        }
      ))
  }

  handlePrintClick = () => {
    this.setState({ poolSelectionLoading: true })
    this.subscriptions.add(
      PoolSelectionListGetObservable(this.props.poolId, 0, 1, 1).subscribe(
        (data) => this.setState({ poolSelection: data.Records[0], showSelectionList: true, poolSelectionLoading: false }),
        (error) => {
          this.setState({ poolSelectionLoading: false })
          toast.error(error)
        }
      ))
  }

  sizeRowItem = (
    name: string,
    valueField: keyof RunRandomData,
    valueValidityField: keyof RunRandomValidity,
    disabled: boolean,
    lg: number,
    xl: number,
    invalidMessage: string
  ) => <Col lg={lg} xl={xl} md={12} className="form-row form-group form-inline">
      <Form.Label htmlFor={`${valueField}id`} className="col-form-label col-6 justify-content-start justify-content-lg-center">{name}</Form.Label>
      <Form.Control type="text"
        id={`${valueField}id`}
        className="col-6"
        value={this.state[valueField]?.toString() || ""}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleSizeChange(valueField, valueValidityField, e.target.value)}
        isInvalid={!this.state[valueValidityField] || false}
        disabled={disabled} />
      <Form.Control.Feedback type="invalid">{invalidMessage}</Form.Control.Feedback>
    </Col>

  sizeRow = (
    name: string,
    statRequiredValue: number | undefined,
    valueField: keyof RunRandomData,
    valueValidityField: keyof RunRandomValidity,
    alternateValueField: keyof RunRandomData,
    alternateValueValidityField: keyof RunRandomValidity,
    showAlternates: boolean,
    disabled: boolean,
    invalidMessage: string,
    alternateInvalidMessage: string
  ) => <Form.Row>
      <Col lg={3} md={12} className="form-row form-group form-inline">
        <div className="col-form-label col-6 justify-content-start">{name}</div>
        <div className="col-form-label col-6 justify-content-start">{statRequiredValue}</div>
      </Col>
      {this.sizeRowItem("ACTUAL TO SELECT", valueField, valueValidityField, disabled, 5, 4, invalidMessage)}
      {showAlternates && this.sizeRowItem("ALTERNATES", alternateValueField, alternateValueValidityField, disabled, 4, 4, alternateInvalidMessage)}
    </Form.Row>

  render() {
    const { statistics, messages } = this.state
    const isPortalAdmin: boolean = this.props.isPortalAdmin || false
    const isCompanyAdmin: boolean = this.props.isCompanyAdmin || false
    const runOnlyByPortalAdmin: boolean = messages?.RunOnlyByPortalAdmin || false

    const allowRun = (isPortalAdmin && runOnlyByPortalAdmin) || (isCompanyAdmin && !runOnlyByPortalAdmin)
    const disableForm = !messages?.IsRunRandomSelection || !allowRun
    return <>
      <Form className="p-4 pt-3" onSubmit={this.handleFormSubmit} hidden={this.state.step !== "run"}>
        {!allowRun && <Form.Row><Col className="error-message">User not Authorized to run randoms for this pool.</Col></Form.Row>}
        <Form.Row><Col><h5>TESTS REQUIRED FOR SELECTION</h5></Col></Form.Row>
        {!this.state.statisticsLoading && <>
          {this.sizeRow("DRUG",
            statistics?.DrugRequired,
            "actualToSelectDrug",
            "isActualToSelectDrugValid",
            "alternateDrug",
            "isAlternateDrugValid",
            statistics?.GenerateAlternates || false,
            disableForm,
            "Drug Actual To Select is greater than pool size",
            "Number of drug Alternates exceeds pool membership count")}
          {this.sizeRow("ALCOHOL",
            statistics?.AlcoholRequired,
            "actualToSelectAlcohol",
            "isActualToSelectAlcoholValid",
            "alternateAlcohol",
            "isAlternateAlcoholValid",
            statistics?.GenerateAlternates || false,
            disableForm,
            "Alcohol Actual To Select is greater than pool size",
            "Number of alcohol Alternates exceeds pool membership count")}
        </>}
        {(this.state.statisticsLoading || this.state.messagesLoading) && <Row><Col className="text-center"><Spinner animation="border" variant="secondary" /></Col></Row>}
        {this.state.messages && this.state.messages.IsRunRandomSelection && this.state.messages.SkipPeriodMessage && <Form.Row><Col className="alert-message">{this.state.messages.SkipPeriodMessage}</Col></Form.Row>}
        {this.state.messages && this.state.messages.IsRunRandomSelection && this.state.messages.RunningPeriodMessage && <Form.Row><Col className="alert-message">{this.state.messages.RunningPeriodMessage}</Col></Form.Row>}
        {this.state.messages && !this.state.messages.IsRunRandomSelection && this.state.messages.RandomSelectionMessage && <Form.Row><Col className="alert-message">{this.state.messages.RandomSelectionMessage}</Col></Form.Row>}
        <Form.Row className="buttons-block left">
          <Button variant="primary" type="submit" disabled={disableForm}>
            {this.state.saving ? <Spinner animation="border" size="sm" /> : "RUN SELECTION"}
          </Button>
        </Form.Row>
      </Form>
      <Container fluid className="p-4 pt-3" hidden={this.state.step !== "success"}>
        <Row><Col>Random Selection complete!</Col></Row>
        <Row>
          <Col>
            <span>You may</span>
            <span className="text-link px-1" onClick={this.handlePrintClick}>Print the Random Selection Master List</span>
            {this.state.poolSelectionLoading && <><Spinner animation="border" size="sm" className="px-1" /></>}
            <span>for the selected tests or press finish to complete the selection process and report the selections.</span>
          </Col>
        </Row>
        <Row><Col>The following reporting of this selection process will be performed:</Col></Row>
        <Row>
          <Col>
            <Table responsive className="table-sm dark">
              <thead>
                <tr className="text-normal">
                  <th>COMPANIES</th>
                  <th>EMAIL</th>
                  <th>FAX</th>
                  <th>FORMFOX</th>
                  <th>CISNET</th>
                  <th>LAST UPDATE</th>
                </tr>
              </thead>
              <tbody>
                {this.state.runResults && this.state.runResults.map((row: RandomSelectionRunData, i: number) =>
                  <tr key={i}>
                    <td>{row.Companies}</td>
                    <td>{row.Email ? "Yes" : "No"}</td>
                    <td>{row.Fax ? "Yes" : "No"}</td>
                    <td>{row.FormFox}</td>
                    <td>{row.CISNET}</td>
                    <td>{FormatDate(row.LastUpdate)}</td>
                  </tr>)}
                {!this.state.runResults && <tr><td colSpan={6}>NO DATA FOUND</td></tr>}
              </tbody>
            </Table>
          </Col>
        </Row>
        <Row>
          <Col className="buttons-block">
            <Button variant="primary" onClick={() => this.props.onRunFinish()}>FINISH</Button>
          </Col>
        </Row>
      </Container>
      <ConfirmationModal show={this.state.showRunConfirmation}
        title="CONFIRM RUN RANDOM"
        text="Are you sure you want to run random selection process?"
        showImage={false}
        onConfirm={this.runSelection}
        onHide={() => this.setState({ showRunConfirmation: false })} />
      {this.state.showSelectionList && this.state.poolSelection &&
        <RandomSelectionList show={this.state.showSelectionList}
          onHide={() => this.setState({ showSelectionList: false })}
          poolId={this.props.poolId}
          poolName={this.props.poolName}
          poolSelection={this.state.poolSelection} 
          unmaskDonorId={0}/>}
    </>
  }
}
